import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dumbbell, Apple, Plus, Trash2, X, BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DietWorkout() {
  const [tab, setTab] = useState("workout");
  const [members, setMembers] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [foods, setFoods] = useState([]);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [dietPlans, setDietPlans] = useState([]);
  const [showWorkout, setShowWorkout] = useState(false);
  const [showDiet, setShowDiet] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [m, ex, fo, wp, dp] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/members`),
        axios.get(`${BACKEND_URL}/api/exercises`),
        axios.get(`${BACKEND_URL}/api/foods`),
        axios.get(`${BACKEND_URL}/api/workout-plans`),
        axios.get(`${BACKEND_URL}/api/diet-plans`),
      ]);
      setMembers(m.data); setExercises(ex.data); setFoods(fo.data); setWorkoutPlans(wp.data); setDietPlans(dp.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const delWorkout = async (id) => { await axios.delete(`${BACKEND_URL}/api/workout-plans/${id}`); fetchAll(); };
  const delDiet = async (id) => { await axios.delete(`${BACKEND_URL}/api/diet-plans/${id}`); fetchAll(); };

  const muscles = [...new Set(exercises.map((e) => e.muscle))];

  return (
    <div data-testid="plans-page" className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-4xl font-serif text-text_primary mb-2">Diet & Workout Plans</h1>
        <p className="text-text_secondary text-sm">Build workout and diet plans, assign them to members, and manage libraries.</p>
      </div>

      <div className="flex gap-2 border-b border-border_soft pb-3 overflow-x-auto">
        {[["workout", "Workout Plans"], ["diet", "Diet Plans"], ["exlib", "Exercise Library"], ["foodlib", "Food Library"]].map(([k, label]) => (
          <button key={k} data-testid={`plans-tab-${k}`} onClick={() => setTab(k)} className={`text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${tab === k ? "bg-gold/15 text-gold border border-gold/30" : "text-text_secondary hover:text-text_primary"}`}>{label}</button>
        ))}
      </div>

      {tab === "workout" && (
        <div className="space-y-5">
          <button data-testid="new-workout-btn" onClick={() => setShowWorkout(true)} className="bg-primary text-[#111827] px-5 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={18} /> New Workout Plan</button>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {workoutPlans.length === 0 && <p className="text-text_secondary italic">No workout plans yet.</p>}
            {workoutPlans.map((p) => (
              <div key={p.id} className="bg-card_bg border border-border_soft rounded-xl p-5">
                <div className="flex justify-between items-start mb-3">
                  <div><h3 className="text-lg font-bold text-text_primary">{p.name}</h3><p className="text-xs text-text_secondary">{p.member_name || "Unassigned"}</p></div>
                  <button onClick={() => delWorkout(p.id)} className="text-text_secondary hover:text-accent_danger"><Trash2 size={16} /></button>
                </div>
                <div className="space-y-1.5">
                  {(p.days || []).map((d, i) => (
                    <div key={i} className="text-sm text-text_secondary flex justify-between bg-main_bg rounded-lg px-3 py-2">
                      <span className="text-text_primary">{d.exercise}</span>
                      <span>{d.day ? d.day + " · " : ""}{d.sets}×{d.reps} {d.weight ? `@${d.weight}kg` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "diet" && (
        <div className="space-y-5">
          <button data-testid="new-diet-btn" onClick={() => setShowDiet(true)} className="bg-primary text-[#111827] px-5 py-3 rounded-xl font-bold flex items-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={18} /> New Diet Plan</button>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {dietPlans.length === 0 && <p className="text-text_secondary italic">No diet plans yet.</p>}
            {dietPlans.map((p) => {
              const totCal = (p.meals || []).reduce((s, m) => s + (parseFloat(m.calories) || 0), 0);
              return (
                <div key={p.id} className="bg-card_bg border border-border_soft rounded-xl p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div><h3 className="text-lg font-bold text-text_primary">{p.name}</h3><p className="text-xs text-text_secondary">{p.member_name || "Unassigned"} · {Math.round(totCal)} kcal</p></div>
                    <button onClick={() => delDiet(p.id)} className="text-text_secondary hover:text-accent_danger"><Trash2 size={16} /></button>
                  </div>
                  <div className="space-y-1.5">
                    {(p.meals || []).map((m, i) => (
                      <div key={i} className="text-sm text-text_secondary flex justify-between bg-main_bg rounded-lg px-3 py-2">
                        <span className="text-text_primary">{m.meal}: {m.food}</span>
                        <span>{m.calories} kcal · P{m.protein}/C{m.carbs}/F{m.fat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "exlib" && <Library title="Exercise Library" icon={Dumbbell} items={exercises} columns={["name", "muscle"]} groups={muscles} groupKey="muscle" />}
      {tab === "foodlib" && <Library title="Food Library" icon={Apple} items={foods} columns={["name", "calories", "protein", "carbs", "fat"]} />}

      {showWorkout && <WorkoutBuilder members={members} exercises={exercises} onClose={() => setShowWorkout(false)} onSaved={() => { setShowWorkout(false); fetchAll(); }} />}
      {showDiet && <DietBuilder members={members} foods={foods} onClose={() => setShowDiet(false)} onSaved={() => { setShowDiet(false); fetchAll(); }} />}
    </div>
  );
}

const Library = ({ title, icon: Icon, items, columns, groupKey }) => (
  <div className="bg-card_bg border border-border_soft rounded-xl p-6">
    <h3 className="text-base font-semibold text-text_primary mb-4 flex items-center gap-2"><Icon size={18} className="text-gold" /> {title} <span className="text-text_secondary text-sm">({items.length})</span></h3>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
      {items.map((it) => (
        <div key={it.id} className="bg-main_bg border border-border_soft/60 rounded-lg px-3 py-2.5">
          <p className="text-sm text-text_primary font-medium">{it.name}</p>
          <p className="text-[11px] text-text_secondary">{groupKey ? it[groupKey] : `${it.calories} kcal · P${it.protein}`}</p>
        </div>
      ))}
    </div>
  </div>
);

const WorkoutBuilder = ({ members, exercises, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [member, setMember] = useState("");
  const [rows, setRows] = useState([{ day: "Day 1", exercise: exercises[0]?.name || "", sets: 3, reps: 10, weight: "", rest: "60s" }]);
  const addRow = () => setRows([...rows, { day: "Day 1", exercise: exercises[0]?.name || "", sets: 3, reps: 10, weight: "", rest: "60s" }]);
  const upd = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const save = async (e) => {
    e.preventDefault();
    const mem = members.find((m) => m.id === member);
    await axios.post(`${BACKEND_URL}/api/workout-plans`, { name, member_id: member, member_name: mem?.name || "", days: rows });
    onSaved();
  };
  return (
    <Modal onClose={onClose} title="New Workout Plan">
      <form onSubmit={save} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <input required placeholder="Plan name" value={name} onChange={(e) => setName(e.target.value)} className="bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm" />
          <select value={member} onChange={(e) => setMember(e.target.value)} className="bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm">
            <option value="">Assign member (optional)</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-6 gap-2">
              <input placeholder="Day" value={r.day} onChange={(e) => upd(i, "day", e.target.value)} className="bg-main_bg border border-border_soft rounded-lg p-2 text-text_primary text-xs" />
              <select value={r.exercise} onChange={(e) => upd(i, "exercise", e.target.value)} className="bg-main_bg border border-border_soft rounded-lg p-2 text-text_primary text-xs col-span-2">
                {exercises.map((ex) => <option key={ex.id} value={ex.name}>{ex.name}</option>)}
              </select>
              <input type="number" placeholder="Sets" value={r.sets} onChange={(e) => upd(i, "sets", e.target.value)} className="bg-main_bg border border-border_soft rounded-lg p-2 text-text_primary text-xs" />
              <input type="number" placeholder="Reps" value={r.reps} onChange={(e) => upd(i, "reps", e.target.value)} className="bg-main_bg border border-border_soft rounded-lg p-2 text-text_primary text-xs" />
              <input placeholder="kg" value={r.weight} onChange={(e) => upd(i, "weight", e.target.value)} className="bg-main_bg border border-border_soft rounded-lg p-2 text-text_primary text-xs" />
            </div>
          ))}
        </div>
        <button type="button" onClick={addRow} className="text-gold text-sm font-semibold flex items-center gap-1"><Plus size={14} /> Add exercise</button>
        <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-3 rounded-xl hover:bg-primary_hover transition-colors">Save Plan</button>
      </form>
    </Modal>
  );
};

const DietBuilder = ({ members, foods, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [member, setMember] = useState("");
  const [rows, setRows] = useState([{ meal: "Breakfast", food: foods[0]?.name || "", qty: 1, calories: foods[0]?.calories || 0, protein: foods[0]?.protein || 0, carbs: foods[0]?.carbs || 0, fat: foods[0]?.fat || 0 }]);
  const addRow = () => setRows([...rows, { meal: "Lunch", food: foods[0]?.name || "", qty: 1, calories: foods[0]?.calories || 0, protein: foods[0]?.protein || 0, carbs: foods[0]?.carbs || 0, fat: foods[0]?.fat || 0 }]);
  const pickFood = (i, foodName) => {
    const f = foods.find((x) => x.name === foodName);
    setRows(rows.map((r, idx) => idx === i ? { ...r, food: foodName, calories: f?.calories || 0, protein: f?.protein || 0, carbs: f?.carbs || 0, fat: f?.fat || 0 } : r));
  };
  const updMeal = (i, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, meal: v } : r));
  const save = async (e) => {
    e.preventDefault();
    const mem = members.find((m) => m.id === member);
    await axios.post(`${BACKEND_URL}/api/diet-plans`, { name, member_id: member, member_name: mem?.name || "", meals: rows });
    onSaved();
  };
  return (
    <Modal onClose={onClose} title="New Diet Plan">
      <form onSubmit={save} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <input required placeholder="Plan name" value={name} onChange={(e) => setName(e.target.value)} className="bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm" />
          <select value={member} onChange={(e) => setMember(e.target.value)} className="bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm">
            <option value="">Assign member (optional)</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </div>
        <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-5 gap-2 items-center">
              <select value={r.meal} onChange={(e) => updMeal(i, e.target.value)} className="bg-main_bg border border-border_soft rounded-lg p-2 text-text_primary text-xs">
                {["Breakfast", "Lunch", "Snack", "Dinner"].map((m) => <option key={m}>{m}</option>)}
              </select>
              <select value={r.food} onChange={(e) => pickFood(i, e.target.value)} className="bg-main_bg border border-border_soft rounded-lg p-2 text-text_primary text-xs col-span-2">
                {foods.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
              </select>
              <span className="text-xs text-text_secondary">{r.calories} kcal</span>
              <span className="text-xs text-text_secondary">P{r.protein}/C{r.carbs}/F{r.fat}</span>
            </div>
          ))}
        </div>
        <button type="button" onClick={addRow} className="text-gold text-sm font-semibold flex items-center gap-1"><Plus size={14} /> Add meal item</button>
        <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-3 rounded-xl hover:bg-primary_hover transition-colors">Save Plan</button>
      </form>
    </Modal>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 admin-scope">
    <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-2xl relative shadow-2xl max-h-[90vh] overflow-y-auto custom-scrollbar">
      <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary"><X size={20} /></button>
      <h2 className="text-2xl font-serif text-text_primary mb-6">{title}</h2>
      {children}
    </div>
  </div>
);
